// Step-1 Geo field for Google Ads copies: suggests from Google's own geo target
// list as the user types (any country), and still accepts a name Google did
// not suggest.
//
// Value shape (CopyCampaignWorkflow's `searchGeo`):
//   picked:   [{ resourceName, label }]  chosen suggestions. Sent on as their
//             resource name, so the copy targets exactly that place.
//   included: 'name\nname'               typed names, one per line (the field's
//             original shape, so saved drafts still load). The copy build
//             matches these against Google and the review step shows the match.
//   pending:  'text'                     what is still in the search box. It is part
//             of the value, not local state, so text the user typed but never
//             explicitly added is still sent (as a typed name) and saved with the
//             draft -- leaving the field cannot silently drop it.
import React, { useEffect, useRef, useState } from 'react';
import { geoTargetLabel } from '../CampaignLaunchWorkflow/gads/gadsRowBuilders';

const DEBOUNCE_MS = 250;
const MIN_CHARS = 2;

const typedLines = (text) => String(text || '').split('\n').map((l) => l.trim()).filter(Boolean);

// What the field sends as the copy's `geoOverrides.included`: picked suggestions as
// their geo target resource name (targeted exactly), then the typed names (matched
// when the copy is built).
export const geoOverrideIncluded = (value) => [
  ...(Array.isArray(value?.picked) ? value.picked : []).map((p) => p.resourceName).filter(Boolean),
  ...typedLines(value?.included),
  ...typedLines(value?.pending).filter((t) => !typedLines(value?.included).includes(t)),
];

// The same wording the review grid uses for a matched target.
export const suggestionLabel = (row) => geoTargetLabel(
  { name: row.name, canonical_name: row.canonicalName, target_type: row.type },
  String(row.resourceName || '').split('/').pop(),
);

export default function CopyGeoSuggest({ value, onChange, suggest }) {
  const picked = Array.isArray(value?.picked) ? value.picked : [];
  const typed = typedLines(value?.included);
  const query = String(value?.pending || '');
  const setQuery = (text) => onChange({ ...value, pending: text });
  // Results are kept WITH the query they answer, and only offered while that is
  // still the query: otherwise a slow reply for "dubai" stays pickable after the
  // box says "Bern".
  const [answer, setAnswer] = useState({ query: '', items: [] });
  const [status, setStatus] = useState('idle'); // idle | loading | ready | error
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const requestRef = useRef(null);

  useEffect(() => {
    const name = query.trim();
    if (requestRef.current) requestRef.current.abort();
    if (name.length < MIN_CHARS || !suggest) {
      setAnswer({ query: '', items: [] });
      setStatus('idle');
      return undefined;
    }
    const controller = new AbortController();
    requestRef.current = controller;
    setStatus('loading');
    const timer = setTimeout(() => {
      suggest(name, controller.signal)
        .then((items) => {
          if (controller.signal.aborted) return;
          setAnswer({ query: name, items: items.filter((r) => !picked.some((p) => p.resourceName === r.resourceName)) });
          setStatus('ready');
          setActive(0);
        })
        .catch(() => {
          if (!controller.signal.aborted) { setAnswer({ query: name, items: [] }); setStatus('error'); }
        });
    }, DEBOUNCE_MS);
    return () => { clearTimeout(timer); controller.abort(); };
    // `picked` is read only to hide what is already chosen; refetching on every
    // pick would re-query Google for the same text.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, suggest]);

  // Keep the highlighted option visible as the arrow keys move through a list
  // longer than its scroll box: Enter must never pick something off-screen.
  useEffect(() => {
    if (!open) return;
    const el = document.getElementById(`cw-geo-opt-${active}`);
    if (el && typeof el.scrollIntoView === 'function') el.scrollIntoView({ block: 'nearest' });
  }, [active, open]);

  const emit = (nextPicked, nextTyped, pending = query) => onChange({
    ...value, picked: nextPicked, included: nextTyped.join('\n'), pending });
  const clearSearch = () => { setAnswer({ query: '', items: [] }); setStatus('idle'); setActive(0); };
  const pick = (row) => {
    emit([...picked, { resourceName: row.resourceName, label: suggestionLabel(row) }], typed, '');
    clearSearch();
  };
  const addTyped = () => {
    const name = query.trim();
    emit(picked, name && !typed.includes(name) ? [...typed, name] : typed, '');
    clearSearch();
  };
  // Suggestions first, then "use what I typed" as the last option.
  const results = answer.query === query.trim() ? answer.items : [];
  const options = [...results.map((row) => ({ kind: 'pick', row })),
    ...(query.trim() ? [{ kind: 'typed' }] : [])];
  const choose = (opt) => (opt.kind === 'pick' ? pick(opt.row) : addTyped());

  return (
    <div className="cw-geo-suggest">
      {(picked.length > 0 || typed.length > 0) && (
        <ul className="cw-geo-chips" aria-label="Target locations">
          {picked.map((p) => (
            <li key={p.resourceName} className="cw-geo-chip">
              <span>{p.label}</span>
              <button type="button" aria-label={`Remove ${p.label}`}
                onClick={() => emit(picked.filter((x) => x.resourceName !== p.resourceName), typed)}>×</button>
            </li>
          ))}
          {typed.map((t) => (
            <li key={`typed:${t}`} className="cw-geo-chip cw-geo-chip-typed">
              <span>{t}</span>
              <small>matched when the copy is built</small>
              <button type="button" aria-label={`Remove ${t}`}
                onClick={() => emit(picked, typed.filter((x) => x !== t))}>×</button>
            </li>
          ))}
        </ul>
      )}
      <div className="cw-geo-input">
      <input
        className="need-in"
        role="combobox"
        aria-label="Add a target location"
        aria-expanded={open && options.length > 0}
        aria-controls="cw-geo-options"
        aria-autocomplete="list"
        aria-activedescendant={open && options[active] ? `cw-geo-opt-${active}` : undefined}
        placeholder="Search Google locations, e.g. Dubai, Ontario, Germany"
        value={query}
        onChange={(e) => { setQuery(e.target.value); setOpen(true); setActive(0); }}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        onKeyDown={(e) => {
          if (e.key === 'Escape') setOpen(false);
          if (e.key === 'ArrowDown') { e.preventDefault(); setOpen(true); setActive((i) => Math.min(i + 1, options.length - 1)); }
          if (e.key === 'ArrowUp') { e.preventDefault(); setActive((i) => Math.max(0, i - 1)); }
          if (e.key === 'Enter' && options[active]) { e.preventDefault(); choose(options[active]); }
        }}
      />
      {open && options.length > 0 && (
        <div className="cw-geo-options" id="cw-geo-options" role="listbox">
          {options.map((opt, i) => (
            <div
              key={opt.kind === 'pick' ? opt.row.resourceName : 'typed'}
              id={`cw-geo-opt-${i}`}
              role="option"
              aria-selected={i === active}
              className={i === active ? 'active' : ''}
              onMouseDown={(e) => { e.preventDefault(); choose(opt); }}
            >
              {opt.kind === 'pick'
                ? suggestionLabel(opt.row)
                : <>Use “{query.trim()}” as typed <small>— matched when the copy is built</small></>}
            </div>
          ))}
        </div>
      )}
      </div>
      {!open && query.trim() && (
        <span className="fieldnote">“{query.trim()}” will be added as typed and matched when the copy is built.</span>
      )}
      {status === 'loading' && <span className="fieldnote">Searching Google locations…</span>}
      {status === 'error' && (
        <span className="fieldnote">Google suggestions are unavailable right now. You can still add the location as typed.</span>
      )}
    </div>
  );
}
